// FILE: /src/Components/Publishing/CreateLinkModal.js (FULLY UPDATED)

import React, { useState } from 'react';
import { Modal, Input, Button, Checkbox, List, message, Spin, Tag, Avatar, Switch } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import axiosServices from '../../services/axios';
import { useSelector } from 'react-redux';
import { CheckCircleFilled, LockOutlined } from '@ant-design/icons';

const CreateLinkModal = ({ isOpen, onClose, posts }) => {
    const [selectedPostIds, setSelectedPostIds] = useState([]);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(null);
    // --- START: NEW STATE FOR PASSWORD ---
    const [isPasswordProtected, setIsPasswordProtected] = useState(false);
    const [password, setPassword] = useState('');
    // --- END: NEW STATE ---

    const { selectedGroup, groups } = useSelector((state) => state.groups);
    const selectedGroupId = groups.find(g => g.name === selectedGroup)?._id;

    const handleSelectPost = (postId) => {
        setSelectedPostIds(prev =>
            prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
        );
    };

    const handleGenerateLink = async () => {
        if (!title.trim()) {
            message.warning('Please provide a title for your link.');
            return;
        }
        if (selectedPostIds.length === 0) {
            message.warning('Please select at least one post to share.');
            return;
        }
        // --- NEW: Password validation ---
        if (isPasswordProtected && !password.trim()) {
            message.warning('Please enter a password for the protected link.');
            return;
        }

        setLoading(true);
        try {
            // --- NEW: Include password in payload ---
            const payload = {
                title,
                postIds: selectedPostIds,
                groupId: selectedGroupId,
            };
            if (isPasswordProtected) {
                payload.password = password;
            }
            
            const response = await axiosServices.post('/api/links/create', payload);
            setGeneratedLink(response.data);
        } catch (error) {
            message.error('Failed to create the shareable link.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTitle('');
        setSelectedPostIds([]);
        setGeneratedLink(null);
        setLoading(false);
        // --- NEW: Reset password state on close ---
        setIsPasswordProtected(false);
        setPassword('');
        onClose();
    };

    const shareUrl = generatedLink ? `${window.location.origin}/s/${generatedLink.shareableId}` : '';

    return (
        <Modal
            title={<span className="font-semibold text-lg">Create a Shareable Link</span>}
            open={isOpen}
            onCancel={handleClose}
            footer={null}
            width={700}
            destroyOnClose
        >
            {generatedLink ? (
                <div className="text-center p-8 flex flex-col items-center">
                    <CheckCircleFilled className="text-5xl text-green-500 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Your Link is Ready!</h3>
                    <p className="text-gray-500 mb-6">Share this link with clients or team members for review.</p>
                    <div className="w-full flex items-center gap-2 bg-gray-100 p-2 rounded-lg">
                        <Input value={shareUrl} readOnly className="flex-grow"/>
                        <CopyToClipboard text={shareUrl} onCopy={() => message.success('Link copied!')}>
                            <Button type="primary">Copy</Button>
                        </CopyToClipboard>
                    </div>
                    <Button className="mt-8" onClick={handleClose}>Done</Button>
                </div>
            ) : (
                <div className="space-y-6 p-2">
                    <div>
                        <label className="font-semibold text-gray-700 mb-2 block">Link Title</label>
                        <Input
                            placeholder="e.g., July Campaign - Client Draft 1"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            size="large"
                        />
                    </div>
                    {/* --- START: NEW PASSWORD SECTION --- */}
                    <div className="space-y-2">
                         <div className="flex items-center gap-3">
                            <Switch checked={isPasswordProtected} onChange={setIsPasswordProtected} size="small" />
                            <label className="font-semibold text-gray-700">Enable Password Protection</label>
                        </div>
                        {isPasswordProtected && (
                            <Input.Password
                                placeholder="Enter a password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                prefix={<LockOutlined className="site-form-item-icon" />}
                                className="animate-fade-in"
                            />
                        )}
                    </div>
                    {/* --- END: NEW PASSWORD SECTION --- */}
                    <div>
                        <label className="font-semibold text-gray-700 mb-2 block">Select Posts to Share ({selectedPostIds.length}/{posts.length})</label>
                        <div className="border rounded-lg max-h-80 overflow-y-auto bg-gray-50">
                            <List
                                dataSource={posts}
                                renderItem={(post, index) => {
                                    const isSelected = selectedPostIds.includes(post._id);
                                    const postTitle = post.message ? post.message : `${selectedGroup} - Post #${index + 1}`;
                                    return (
                                        <List.Item
                                            key={post._id}
                                            onClick={() => handleSelectPost(post._id)}
                                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                                            actions={[<Checkbox checked={isSelected} readOnly />]}
                                        >
                                            <List.Item.Meta
                                                avatar={<Avatar shape="square" size={48} src={post.media_url?.[0]} className="bg-gray-200" />}
                                                title={<span className="font-semibold text-gray-800 truncate block max-w-md">{postTitle}</span>}
                                                description={
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Tag color={post.status === 'draft' ? 'gold' : 'blue'}>{post.status}</Tag>
                                                        <span>{post.scheduledTime ? moment(post.scheduledTime).format('MMM D, h:mm A') : 'Unscheduled'}</span>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                         <Button onClick={handleClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={handleGenerateLink}
                            disabled={!title.trim() || selectedPostIds.length === 0}
                        >
                            Generate Link
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default CreateLinkModal;